// frontend/app/components/lessonPlans/present/presentRules.js
//
// What present mode shows, and what it holds back until the teacher asks for
// it. Pure functions, no React and no network — like lessonPlanRules.js, this
// is the part worth unit-testing on its own.
//
// Two ideas live here.
//
// REVEAL, NOT ANSWER. Nobody in the room has a device, so a check block is not
// something to answer — it is something to *ask*. The block shows its question
// and its options, the teacher takes answers out loud, and only then does the
// answer key appear. `revealFor` decides whether a block has anything worth
// that second beat at all; a block it returns null for must never grow a Reveal
// control, because a control that does nothing is worse than no control.
//
// NOTES ARE NOT CONTENT. One screen is mirrored to the projector, so the
// teacher's answer key and the misconception-to-watch-for are read by thirty
// students the moment they are drawn. `presentBlocks` therefore routes
// teacher_tip out of the slide and into the notes panel, which starts closed.

/**
 * Blocks that never reach the slide, for reasons other than being a note.
 *
 * mastery_map renders one student's own concept bands. In present mode there is
 * no student logged in and no `mastery` payload to render — and a class of
 * thirty has thirty different maps, so there is no honest single answer to
 * draw. It is dropped rather than rendered empty: "We'll fill this in as you
 * work" means nothing to a room.
 */
export const PRESENT_HIDDEN_BLOCK_TYPES = ["mastery_map"];

/** Blocks that move from the slide into the (closed by default) notes panel. */
export const TEACHER_NOTE_BLOCK_TYPES = ["teacher_tip"];

/**
 * Every type present mode can draw. Anything else — a type this build has never
 * heard of, arriving from a newer authoring screen — is filtered out here
 * rather than rendered as the workspace's dashed "Unsupported content block"
 * box. That box is right for an author, who needs to see the gap, and wrong for
 * a wall in front of thirty students.
 *
 * Filtering here rather than in the renderer is what lets `stepHasContent` be
 * honest: a step whose only block is an unknown type has nothing to present,
 * and must say so instead of drawing a title over an empty screen.
 */
export const PRESENTABLE_BLOCK_TYPES = [
  "rich_text",
  "bullet_list",
  "callout",
  "ai_prompt",
  "saras_pwo",
  "saras_video",
  "saras_topic_quiz",
  "multiple_choice",
  "multi_select",
  "ordering",
  "short_answer",
  "prediction",
  "explanation",
  "exploration_probe",
  "reflection",
  "next_challenge",
];

const asArray = (value) => (Array.isArray(value) ? value : []);

const textOf = (value) =>
  typeof value === "string" && value.trim().length > 0 ? value.trim() : null;

/**
 * A stable identity for one block on one slide.
 *
 * Authored blocks normally carry an id, but the workspace already tolerates one
 * that does not, and reveal order is tracked by this key — two blocks colliding
 * on a key would reveal each other. The index makes that impossible.
 */
export const presentBlockKey = (step, block, index) =>
  block?.id || `${step?.step_key || "step"}-${block?.type || "block"}-${index}`;

/** The blocks that go on the slide, in authored order. */
export const presentBlocks = (step) =>
  asArray(step?.blocks).filter(
    (block) =>
      block &&
      typeof block === "object" &&
      !TEACHER_NOTE_BLOCK_TYPES.includes(block.type) &&
      !PRESENT_HIDDEN_BLOCK_TYPES.includes(block.type) &&
      PRESENTABLE_BLOCK_TYPES.includes(block.type)
  );

/**
 * Everything addressed to the teacher rather than to the room: the step's own
 * guidance, what the students are meant to be doing, and any teacher_tip
 * blocks. The caller keeps this behind the notes toggle.
 */
export const stepNotes = (step) => ({
  guidance: textOf(step?.teacher_guidance),
  studentActivity: textOf(step?.student_activity),
  tips: asArray(step?.blocks)
    .filter((block) => TEACHER_NOTE_BLOCK_TYPES.includes(block?.type))
    .map((block) => ({ key: block.id, text: textOf(block.text) }))
    .filter((tip) => tip.text),
});

export const hasNotes = (step) => {
  const notes = stepNotes(step);
  return Boolean(notes.guidance || notes.studentActivity || notes.tips.length > 0);
};

const optionReveal = (block, correctOptionIds) => {
  const explanation = textOf(block.explanation);
  // An authored key with no correct option and no explanation has nothing to
  // show on the second beat, however answerable it looks.
  if (correctOptionIds.length === 0 && !explanation) return null;
  return {
    kind: "options",
    correctOptionIds,
    sequence: [],
    notes: [],
    label: null,
    explanation,
  };
};

const guidanceReveal = (label, notes) => {
  const cleaned = notes.map(textOf).filter(Boolean);
  if (cleaned.length === 0) return null;
  return {
    kind: "guidance",
    correctOptionIds: [],
    sequence: [],
    notes: cleaned,
    label,
    explanation: null,
  };
};

/**
 * What this block reveals on the second beat, or null if it reveals nothing.
 *
 * Free text is the interesting case. `short_answer`, `prediction` and
 * `reflection` carry no authored answer — a prediction is explicitly never
 * right or wrong — so they are prompts for discussion and nothing more. An
 * `explanation` block's rubric and a probe's hint ARE authored guidance, and
 * they are exactly what a teacher wants on the wall once the room has had its
 * say, so those two do reveal.
 */
export const revealFor = (block) => {
  if (!block || typeof block !== "object") return null;

  switch (block.type) {
    case "multiple_choice":
      return optionReveal(
        block,
        block.correct_option_id ? [block.correct_option_id] : []
      );

    case "multi_select":
      return optionReveal(block, asArray(block.correct_option_ids));

    case "ordering": {
      const items = asArray(block.items);
      const explanation = textOf(block.explanation);
      const sequence = asArray(block.correct_order)
        .map((id) => {
          const item = items.find((candidate) => candidate?.id === id);
          return item ? { id, text: item.text } : null;
        })
        .filter(Boolean);
      if (sequence.length === 0 && !explanation) return null;
      return {
        kind: "sequence",
        correctOptionIds: [],
        sequence,
        notes: [],
        label: null,
        explanation,
      };
    }

    // The rubric is the answer key for a spoken explanation. The authoring
    // screen warns that students never see it, which is a rule about the
    // self-study runtime where a student could open it before answering — here
    // it cannot appear until the teacher has taken answers from the room.
    case "explanation":
      return guidanceReveal("What a strong answer includes", asArray(block.rubric));

    case "exploration_probe":
      return guidanceReveal("If the room is stuck", [block.hint]);

    default:
      return null;
  }
};

export const hasReveal = (block) => revealFor(block) !== null;

/**
 * The keys of this step's revealable blocks, in the order the teacher will walk
 * them. One press of the clicker reveals the next one; when the list is spent,
 * the same press moves to the next step — the PowerPoint build-step model.
 */
export const revealableBlockIds = (step) =>
  presentBlocks(step)
    .map((block, index) => ({ block, key: presentBlockKey(step, block, index) }))
    .filter(({ block }) => hasReveal(block))
    .map(({ key }) => key);

/** Does this step put anything on the wall at all? */
export const stepHasContent = (step) => presentBlocks(step).length > 0;
